/**
 * eventListeners.js
 * Central event listener setup and handler functions
 */

import { elements } from './dom.js?v=302';
import { state } from './state.js?v=302';
import { toggleTheme, saveThemeSelection, applyTheme } from './themes.js?v=302';
import { toggleSidebar, showToast, handleScroll, scrollToBottom, renderChatList } from './ui.js?v=302';
import { createNewChat, sendMessage, filterChatList } from './chat.js?v=302';
import { handleInputKeydown, autoResizeInput } from './utils.js?v=302';
import { handleKeyboardShortcuts, trapFocus } from './keyboard.js?v=302';
import { saveChats } from './storage.js?v=302';
import { toggleVoiceMode, updateMicButton } from './tts.js?v=302';

/**
 * Export and import chat data
 */
export function exportChats() {
  const data = {
    chatbot_chats: state.chats,
    chatbot_current_chat: state.currentChatId,
  };

  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: "application/json" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = "eliza-chatbot-backup.json";
  a.click();

  URL.revokeObjectURL(url);
  showToast("Chats exported");
}

export function importChats() {
  const file = elements.importFileInput.files[0];
  if (!file) {
    showToast("Please select a file", "error");
    return;
  }

  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const data = JSON.parse(e.target.result);

      // Validate required keys and types
      if (typeof data !== "object" || data === null) {
        throw new Error("Invalid format");
      }
      if (
        !Array.isArray(data.chatbot_chats) &&
        data.chatbot_chats !== undefined
      ) {
        throw new Error("Invalid chatbot_chats");
      }
      if (
        typeof data.chatbot_current_chat !== "string" &&
        data.chatbot_current_chat !== null &&
        data.chatbot_current_chat !== undefined
      ) {
        throw new Error("Invalid chatbot_current_chat");
      }

      // Replace data (not merge)
      if (data.chatbot_chats !== undefined) {
        state.chats = data.chatbot_chats;
        localStorage.setItem(
          "chatbot_chats",
          JSON.stringify(data.chatbot_chats),
        );
      }
      if (data.chatbot_current_chat !== undefined) {
        // Validate chat exists
        const chatExists = state.chats.some(
          (c) => c.id === data.chatbot_current_chat,
        );
        state.currentChatId = chatExists
          ? data.chatbot_current_chat
          : null;
        if (state.currentChatId) {
          localStorage.setItem(
            "chatbot_current_chat",
            data.chatbot_current_chat,
          );
        } else {
          localStorage.removeItem("chatbot_current_chat");
        }
      }

      // Refresh UI
      renderChatList();

      // Update connection status dynamically
      import('./app.js?v=302').then(({ updateConnectionStatus }) => {
        if (updateConnectionStatus) updateConnectionStatus();
      }).catch(() => {});

      elements.importModal.classList.remove("visible");
      showToast("Chats imported");
    } catch (error) {
      showToast("Error importing file: " + error.message, "error");
    }
  };
  reader.readAsText(file);
}

/**
 * Close settings modal
 */
export function closeSettingsModal() {
  elements.settingsModal.classList.remove("visible");
}

/**
 * Save draft message to localStorage
 * Debounced to avoid excessive writes
 */
export function saveDraft() {
  clearTimeout(state.draftTimeout);
  state.draftTimeout = setTimeout(() => {
    const draftKey = `chatbot_draft_${state.currentChatId || 'default'}`;
    const value = elements.input.value.trim();
    if (value) {
      localStorage.setItem(draftKey, value);
      showDraftSavedIndicator();
    } else {
      localStorage.removeItem(draftKey);
    }
  }, 500); // 500ms debounce
}

/**
 * Show draft saved indicator
 */
function showDraftSavedIndicator() {
  let indicator = document.getElementById('draft-indicator');
  if (!indicator) {
    indicator = document.createElement('div');
    indicator.id = 'draft-indicator';
    indicator.className = 'draft-saved-indicator';
    indicator.textContent = 'Draft saved';
    elements.input.parentElement.style.position = 'relative';
    elements.input.parentElement.appendChild(indicator);
  }
  indicator.classList.add('visible');
  setTimeout(() => indicator.classList.remove('visible'), 2000);
}

/**
 * Setup all event listeners for the application
 * Called once during initialization
 */
export function setupEventListeners() {
  // Global: force all links in messages to open in new tabs
  // and make images clickable (opens full size in new tab)
  document.addEventListener('click', (e) => {
    const msgContent = e.target.closest('.message-content');
    if (!msgContent) return;

    // Handle link clicks: open in new tab
    const link = e.target.closest('a[href]');
    if (link && msgContent.contains(link)) {
      e.preventDefault();
      window.open(link.href, '_blank', 'noopener,noreferrer');
      return;
    }

    // Handle image clicks: open full size in new tab
    if (e.target.tagName === 'IMG' && e.target.src) {
      e.preventDefault();
      e.stopPropagation();
      window.open(e.target.src, '_blank', 'noopener,noreferrer');
      return;
    }
  });

  // Theme toggle
  elements.themeBtn.addEventListener("click", toggleTheme);

  // Sidebar toggle
  elements.toggleSidebarBtn.addEventListener("click", toggleSidebar);
  elements.sidebarOverlay.addEventListener("click", toggleSidebar);

  // New chat
  elements.newChatBtn.addEventListener("click", createNewChat);

  // User menu (opens settings)
  const userMenuBtn = document.getElementById("user-menu-btn");
  if (userMenuBtn) {
    userMenuBtn.addEventListener("click", () => {
      elements.settingsModal.classList.add("visible");
      trapFocus(elements.settingsModal);
    });
  }

  // Send message or cancel generation
  elements.sendBtn.addEventListener("click", () => {
    if (state.isGenerating) {
      // Cancel the current generation
      if (state.controller) {
        state.controller.abort();
      }
    } else {
      // Send message
      sendMessage().catch((err) => {
        console.error("Failed to send message:", err);
        showToast("Failed to send message", "error");
      });
    }
  });
  elements.input.addEventListener("keydown", handleInputKeydown);
  elements.input.addEventListener("input", () => {
    autoResizeInput();
    saveDraft();
  });

  // Voice mode toggle
  const voiceToggleBtn = document.getElementById('voice-toggle-btn');
  if (voiceToggleBtn) {
    voiceToggleBtn.addEventListener('click', toggleVoiceMode);
    // Set initial state
    updateMicButton();
  }

  // File upload - dynamically import file handlers
  elements.uploadBtn.addEventListener("click", () =>
    elements.fileInput.click(),
  );
  elements.fileInput.addEventListener("change", async (e) => {
    const { handleFileUpload } = await import('./files/files.js?v=302');
    handleFileUpload(e);
  });

  // Clipboard paste support for images
  elements.input.addEventListener("paste", async (e) => {
    const clipboardData = e.clipboardData || window.clipboardData;
    if (!clipboardData) return;

    const items = clipboardData.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      // Check if the item is an image
      if (item.type.startsWith('image/')) {
        e.preventDefault(); // Prevent default paste behavior for images
        
        const file = item.getAsFile();
        if (file) {
          // Import the file processing modules
          const { processFile } = await import('./files/processors.js?v=302');
          const { updateAttachments } = await import('./files/files.js?v=302');
          
          try {
            // Generate a filename based on timestamp
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
            const ext = file.type.split('/')[1] || 'png';
            const fileName = `screenshot-${timestamp}.${ext}`;
            
            // Create a new File object with the generated name
            const namedFile = new File([file], fileName, { type: file.type });
            
            // Process the file
            const fileData = await processFile(namedFile);
            state.attachedFiles.push(fileData);
            updateAttachments(state.attachedFiles);
            
            showToast(`Screenshot attached`, "success");
          } catch (error) {
            console.error("Error processing pasted image:", error);
            showToast("Error processing pasted image", "error");
          }
        }
        break; // Only handle the first image
      }
    }
  });

  // Themes tab actions
  const closeThemesBtn = document.getElementById("close-themes-tab-btn");
  if (closeThemesBtn) {
    closeThemesBtn.addEventListener("click", closeSettingsModal);
  }

  // Import/Export
  elements.exportBtn.addEventListener("click", exportChats);
  elements.importBtn.addEventListener("click", () => {
    elements.importModal.classList.add("visible");
    trapFocus(elements.importModal);
  });
  elements.cancelImportBtn.addEventListener("click", () =>
    elements.importModal.classList.remove("visible"),
  );
  elements.confirmImportBtn.addEventListener("click", importChats);

  // Shortcuts modal
  elements.shortcutsBtn.addEventListener("click", () => {
    elements.shortcutsModal.classList.add("visible");
    trapFocus(elements.shortcutsModal);
  });
  elements.closeShortcutsBtn.addEventListener("click", () =>
    elements.shortcutsModal.classList.remove("visible"),
  );

  // Settings modal
  elements.settingsBtn.addEventListener("click", () => {
    elements.settingsModal.classList.add("visible");
    trapFocus(elements.settingsModal);
  });

  elements.settingsModal.addEventListener("click", (e) => {
    if (e.target === elements.settingsModal) {
      closeSettingsModal();
    }
  });

  // Settings tabs switching
  document.querySelectorAll('.settings-tab').forEach(tab => {
    tab.addEventListener('click', () => {
      const targetTab = tab.dataset.tab;
      
      // Update active tab button
      document.querySelectorAll('.settings-tab').forEach(t => t.classList.remove('active'));
      tab.classList.add('active');
      
      // Update active panel
      document.querySelectorAll('.settings-tab-panel').forEach(panel => panel.classList.remove('active'));
      document.getElementById(`${targetTab}-tab-panel`).classList.add('active');
    });
  });

  // Close buttons for each tab
  const closeTypographyBtn = document.getElementById("close-typography-tab-btn");
  if (closeTypographyBtn) {
    closeTypographyBtn.addEventListener("click", closeSettingsModal);
  }

  const closeDataBtn = document.getElementById("close-data-tab-btn");
  if (closeDataBtn) {
    closeDataBtn.addEventListener("click", closeSettingsModal);
  }

  // Search
  elements.searchChats.addEventListener("input", filterChatList);

  // Keyboard shortcuts
  document.addEventListener("keydown", handleKeyboardShortcuts);

  // Close modals on outside click
  document.querySelectorAll(".modal-overlay").forEach((modal) => {
    modal.addEventListener("click", (e) => {
      if (e.target === modal) {
        modal.classList.remove("visible");
      }
    });
  });

  // Save themes button
  const saveThemesBtn = document.getElementById("save-themes-tab-btn");
  if (saveThemesBtn) {
    saveThemesBtn.addEventListener("click", () => {
      saveThemeSelection();
      elements.settingsModal.classList.remove("visible");
    });
  }

  // Delegated event listener for theme selection
  const themesContent = document.getElementById("themes-tab-content");
  if (themesContent) {
    themesContent.addEventListener("change", (e) => {
      if (e.target.name === "theme-selection" && e.target.checked) {
        state.selectedTheme = e.target.value;
      }
    });
  }

  // Scroll listener
  elements.messagesContainer.addEventListener("scroll", handleScroll);

  // Scroll to bottom button click
  const scrollBtn = document.getElementById("scroll-bottom-btn");
  if (scrollBtn) {
    scrollBtn.addEventListener("click", () => {
      scrollToBottom();
    });
  }

  // Font Type Change Handler
  elements.fontTypeSelect.addEventListener("change", async (e) => {
    const selectedFont = e.target.value;
    
    // Dynamically load the font if needed
    const { ensureFontLoaded } = await import('./themes.js?v=302');
    ensureFontLoaded(selectedFont);
    
    document.documentElement.style.setProperty(
      "--message-font-family",
      selectedFont,
    );
    localStorage.setItem("edFontType", selectedFont);
  });

  // Font Size Change Handler
  elements.fontSizeSelect.addEventListener("change", (e) => {
    const selectedSize = e.target.value;
    document.documentElement.style.setProperty(
      "--message-font-size",
      selectedSize,
    );
    localStorage.setItem("edFontSize", selectedSize);
  });

  // Theme Select Change Handler (header dropdown)
  elements.themeSelect.addEventListener("change", (e) => {
    const selectedTheme = e.target.value;
    state.selectedTheme = selectedTheme;
    localStorage.setItem("chatbot_selected_theme", selectedTheme);
    applyTheme();
    // Sync with settings modal radio buttons if open
    const radioBtn = document.querySelector(`input[name="theme-selection"][value="${selectedTheme}"]`);
    if (radioBtn) radioBtn.checked = true;
    showToast("Theme updated");
  });
}
